import React, { useEffect, useRef, useState } from "react";
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
import { Box, Button, ButtonGroup, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";

export default function AppSplitButton({
  label,
  options = [],
  startIcon,
  disabled = false,
  size = "medium",
  variant = "contained",
  onClick,
  menuLabel = "Chọn tác vụ",
  sx,
}) {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pendingOption, setPendingOption] = useState(null);
  const primaryOption = options[0];

  const handlePrimaryClick = () => {
    if (disabled) return;
    (onClick || primaryOption?.onClick)?.();
  };

  const handleMenuItemClick = (option) => {
    if (option?.disabled) return;

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    setPendingOption(option);
    setOpen(false);
  };

  useEffect(() => {
    if (open || !pendingOption) return undefined;

    const nextOption = pendingOption;
    const timeoutId = window.setTimeout(() => {
      nextOption?.onClick?.();
      setPendingOption(null);
    }, 100);

    return () => window.clearTimeout(timeoutId);
  }, [open, pendingOption]);

  return (
    <Box sx={{ display: "inline-flex", ...sx }}>
      <ButtonGroup
        ref={anchorRef}
        variant={variant}
        size={size}
        disabled={disabled}
        sx={{
          boxShadow: "none",
          "& .MuiButton-root": {
            minHeight: size === "small" ? 24 : 30,
            borderRadius: 0,
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "none",
          },
          "& .MuiButtonGroup-firstButton": {
            borderTopLeftRadius: "8px",
            borderBottomLeftRadius: "8px",
            px: "10px",
          },
          "& .MuiButtonGroup-lastButton": {
            borderTopRightRadius: "8px",
            borderBottomRightRadius: "8px",
            minWidth: size === "small" ? "24px" : "30px",
            padding: "4px",
            marginLeft: "2px",
          },
        }}
      >
        <Button startIcon={startIcon} onClick={handlePrimaryClick}>
          {label}
        </Button>
        <Button
          aria-label={menuLabel}
          aria-haspopup="menu"
          aria-expanded={open ? "true" : undefined}
          onClick={() => setOpen((current) => !current)}
        >
          {open ? <ArrowDropUp /> : <ArrowDropDown />}
        </Button>
      </ButtonGroup>
      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            mt: "2px",
            minWidth: anchorRef.current?.clientWidth || 240,
            borderRadius: "8px",
            overflow: "hidden",
            boxShadow: "0 14px 34px rgba(15, 23, 42, 0.16)",
            border: (theme) => `1px solid ${theme.palette.divider}`,
          },
        }}
        MenuListProps={{
          sx: {
            p: 0,
            "& .MuiMenuItem-root": {
              minHeight: 36,
              px: 1,
              py: 0,
            },
          },
        }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.value || option.label}
            disabled={option.disabled}
            onClick={() => handleMenuItemClick(option)}
            sx={{
              minHeight: 52,
              justifyContent: "center",
              gap: 1,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              "&:last-of-type": { borderBottom: 0 },
              "&.Mui-selected, &:hover": {
                bgcolor: "action.hover",
              },
              "& .MuiListItemIcon-root": {
                minWidth: "24px",
              },
            }}
          >
            {option.icon && <ListItemIcon sx={{ minWidth: "24px", width: "24px" }}>{option.icon}</ListItemIcon>}
            <ListItemText
              primary={option.label}
              primaryTypographyProps={{
                fontWeight: 500,
                textAlign: "left",
                whiteSpace: "normal",
                fontSize: "12px",
                padding: 0,
                // padding: "16px"
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
